/**
 * LinkTelegramCard — sidebar widget for logged-in web users.
 *
 * If the user's telegram_id is positive (real TG account) → shows "linked"
 * badge with their TG username.
 *
 * If telegram_id is negative (web-only stub) → shows a "Link my Telegram"
 * collapsible flow:
 *   1. Press button → expanded view
 *   2. Instruction: send `/web` to the bot, paste the 6-char code here
 *   3. POST /api/web/link-telegram with the code
 *   4. On success → store new token, refresh user state, collapse
 *
 * Designed to fit the eldritch red/cyan theme of the sidebar.
 */
import React, { useState } from "react";
import { Send, Link2, Check, Loader2, X } from "lucide-react";
import webApi from "@/lib/webApi";

export default function LinkTelegramCard({ user, onLinked }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const linked = (user?.telegram_id || 0) > 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const c = code.trim().toUpperCase();
    if (c.length < 4) {
      setError("Código muy corto");
      return;
    }
    setSubmitting(true);
    try {
      const r = await webApi.post("/link-telegram", { code: c });
      // Persist the new token so the next request uses the real telegram_id
      try {
        if (r.data?.token) localStorage.setItem("web_token", r.data.token);
      } catch (storageErr) {
        console.debug("[link-tg] token save failed:", storageErr?.message);
      }
      setSuccess(true);
      setCode("");
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
        onLinked?.(r.data?.user);
      }, 1500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === "string") setError(d);
      else if (err?.message === "Network Error")
        setError("Sin conexión. Intenta de nuevo.");
      else setError("No se pudo vincular. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (linked) {
    return (
      <div
        className="mx-3 my-3 px-3 py-2.5 border border-data-cyan/30 rounded-sm flex items-center gap-2"
        style={{
          background: "rgba(0, 30, 40, 0.4)",
          boxShadow: "0 0 14px rgba(0, 224, 255, 0.10)",
        }}
        data-testid="tg-link-badge"
      >
        <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-data-cyan/15 text-data-cyan flex-shrink-0">
          <Send size={13} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-mono tracking-[0.3em] text-data-cyan/80">
            TELEGRAM VINCULADO
          </div>
          <div className="text-[11px] font-mono text-white truncate">
            @{user.username || user.telegram_id}
          </div>
        </div>
        <Check size={14} className="text-data-cyan flex-shrink-0" />
      </div>
    );
  }

  return (
    <div className="mx-3 my-3" data-testid="tg-link-card">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full px-3 py-2.5 border border-data-cyan/30 rounded-sm text-left transition-all hover:border-data-cyan/70 hover:bg-data-cyan/5 flex items-center gap-2"
          style={{ background: "rgba(0, 30, 40, 0.3)" }}
          data-testid="tg-link-open"
        >
          <div className="w-7 h-7 rounded-sm flex items-center justify-center bg-data-cyan/15 text-data-cyan flex-shrink-0">
            <Link2 size={13} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-mono tracking-[0.3em] text-data-cyan/80">
              ¿USAS TELEGRAM?
            </div>
            <div className="text-[11px] font-mono text-zinc-300 truncate">
              Vincular cuenta (opcional)
            </div>
          </div>
        </button>
      ) : (
        <div
          className="p-3 border border-data-cyan/40 rounded-sm relative"
          style={{
            background: "rgba(0, 30, 40, 0.5)",
            boxShadow: "0 0 20px rgba(0, 224, 255, 0.12)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setError("");
              setCode("");
            }}
            className="absolute top-1.5 right-1.5 text-zinc-400 hover:text-data-cyan"
            data-testid="tg-link-close"
            aria-label="Cerrar"
          >
            <X size={12} />
          </button>

          <div className="text-[10px] font-mono tracking-[0.25em] text-data-cyan mb-2">
            ── VINCULAR TELEGRAM ──
          </div>

          {success ? (
            <div
              className="flex items-center gap-2 text-emerald-300 text-[12px] py-3"
              data-testid="tg-link-success"
            >
              <Check size={14} /> Vinculado. Tus créditos se unieron.
            </div>
          ) : (
            <>
              <ol className="text-[10px] font-mono tracking-wide text-zinc-300 space-y-1 mb-3 leading-relaxed">
                <li>
                  <span className="text-data-cyan">1.</span> Envía{" "}
                  <code className="text-data-cyan">/web</code> al bot de
                  Telegram.
                </li>
                <li>
                  <span className="text-data-cyan">2.</span> Pega el código que
                  te entregó:
                </li>
              </ol>

              <form onSubmit={submit} className="space-y-2">
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  maxLength={12}
                  placeholder="ABC123"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="tg-link-code-input"
                  className="w-full bg-black/60 border border-data-cyan/30 px-3 py-2 text-center tracking-[0.5em] font-mono text-base font-bold text-white placeholder:text-zinc-600 focus:outline-none focus:border-data-cyan rounded-sm"
                />
                {error && (
                  <div
                    className="text-[10px] font-mono text-rune-red border border-rune-red/40 px-2 py-1 rounded-sm bg-rune-red/10"
                    data-testid="tg-link-error"
                  >
                    ⚠ {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting || !code.trim()}
                  data-testid="tg-link-submit"
                  className="w-full px-3 py-2 font-runic tracking-[0.3em] text-xs text-white rounded-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01]"
                  style={{
                    background:
                      "linear-gradient(135deg, #00e0ff 0%, #0099bb 50%, #003644 100%)",
                    boxShadow:
                      "0 0 18px rgba(0,224,255,0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
                  }}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={12} className="animate-spin" /> VINCULANDO…
                    </span>
                  ) : (
                    "VINCULAR"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}
